import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import BusinessProfileCard from "../../customer-onboarding/components/BusinessProfileCard";
import FloatingChatButton from "../../../components/ChatButton/FloatingChatButton";

interface DashboardProps {
  onPageChange?: (page: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onPageChange }) => {
  const { idToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);

  useEffect(() => {
    // Only show the alert if coming from a successful onboarding submission
    if (location.state && (location.state as any).showSuccess) {
      setShowSuccess(true);
      setProfileComplete(true); // Mark profile as complete
      // Clear the state so it doesn't show again on refresh or further navigation
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const [isLoading, setIsLoading] = useState(true);

  // Fetch dashboard data
  useEffect(() => {
    // Simulate fetching stats data
    const fetchStats = async () => {
      setIsLoading(true);

      // Simulate API delay
      setTimeout(() => {
        setIsLoading(false);
      }, 800);
    };

    fetchStats();
  }, []);

  // Update handleOnboardingClick to use navigate for routing
  const handleOnboardingClick = () => {
    navigate("/onboarding");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <div className="space-y-6">
        {showSuccess && (
          <div className="flex items-center px-6 py-4 bg-gradient-to-r from-brand-primary to-brand-primary-light rounded-xl shadow-lg text-white font-poppins relative w-full max-w-2xl mx-auto mt-8 transition-transform duration-300 hover:scale-105 hover:shadow-2xl z-50">
            {/* Success alert content unchanged */}
            {/* ... */}
          </div>
        )}

        {/* Dashboard Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Set Up Your Business Profile */}
          {!profileComplete && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
              <div className="p-6 flex-grow">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-brand-secondary">
                    <span className="material-icons text-3xl text-brand-primary">
                      business
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Set Up Your Business Profile
                    </h3>
                    <p className="text-gray-600 mt-1">
                      Set up your business profile and brand details to get the
                      most out of Daisy AI.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <button
                  onClick={handleOnboardingClick}
                  className="w-full bg-brand-primary-light text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 hover:bg-brand-primary-light/90"
                >
                  <span className="material-icons">add_circle_outline</span>
                  Create New Business
                </button>
              </div>
            </div>
          )}

          {/* Right Column - Finish Brand Details */}
          <BusinessProfileCard />
        </div>

        {/* Floating Chat Button - Only on Dashboard */}
        {/* Temporarily hidden - will be displayed later */}
        {/* {idToken && <FloatingChatButton idToken={idToken} />} */}
      </div>
    </div>
  );
};

export default Dashboard;
